import React, { useEffect } from 'react';
import { useAuth } from '../AuthProvider';
import { CircularProgress, Box, Typography } from '@mui/material';

interface ProtectedRouteProps {
  children: React.ReactNode;
  fallbackPath?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  fallbackPath = '/login',
}) => {
  const { isAuthenticated, isLoading } = useAuth();

  // Handle redirect in useEffect to avoid modifying window during render
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // Store the current URL to redirect back after login
      const currentPath = window.location.pathname + window.location.search;
      if (
        currentPath !== '/' &&
        currentPath !== '/login' &&
        currentPath !== '/register'
      ) {
        sessionStorage.setItem('redirectAfterLogin', currentPath);
      }
      window.location.href = fallbackPath;
    }
  }, [isLoading, isAuthenticated, fallbackPath]);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2,
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  // Show nothing while redirecting
  if (!isAuthenticated) {
    return null;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
